/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../Modal/Modal';
import { GhostButton } from '../../styles/ui/Button';
import { CellLink, TextLink } from '../../styles/ui/Link';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';

// ---------------------------------------------------------------------------
// AVISO de possível duplicidade de movimento (handoff 0089; rework 2026-08-16).
//
// Não é modal de erro: a criação nunca é bloqueada. Desde o modal unificado
// de confirmação (`MovementConfirmModal`), este componente é um VIEWER de
// detalhe — aberto pelo "ver" do indicador; a decisão de registrar acontece
// no modal unificado do Salvar, não aqui.
//
// Exporta também:
// - `DuplicatesTable` — a tabela de movimentos similares, reusada pelo modal
//   unificado (componentizada, não copiada);
// - `DuplicateCheckHint` — indicador discreto pra ficar junto do campo
//   Nº Documento.
// ---------------------------------------------------------------------------

const WarnBanner = styled.p`
   margin: 0 0 14px;
   padding: 10px 12px;
   border-radius: 10px;
   border: 1px solid rgba(138, 90, 0, 0.35);
   background: rgba(255, 179, 0, 0.12);
   color: #6b4a00;
   font-size: 13.5px;
   font-weight: 600;
   line-height: 1.45;
`;

const MOVE_TYPE_LABELS = { in: 'Entrada', out: 'Saída' };

function formatDateTimeSaoPaulo(isoUtc) {
   if (!isoUtc) return '--';
   const date = new Date(isoUtc);
   if (Number.isNaN(date.getTime())) return '--';
   return date.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

// Tabela de movimentos similares — usada aqui e no MovementConfirmModal.
export function DuplicatesTable({ duplicates = [] }) {
   return (
      <TableWrapper>
         <Table>
            <thead>
               <tr>
                  <th className="center">ID</th>
                  <th>PDV</th>
                  <th>Tipo doc.</th>
                  <th className="center">Nº doc.</th>
                  <th className="center">Entrada/Saída</th>
                  <th className="center">Data</th>
               </tr>
            </thead>
            <tbody>
               {duplicates.map((movement) => (
                  <tr key={movement.id}>
                     <td className="center">
                        <CellLink
                           as="a"
                           href={`/movements/${movement.id}`}
                           target="_blank"
                           rel="noopener noreferrer"
                           title="Abrir movimento em nova aba"
                        >
                           {movement.id}
                        </CellLink>
                     </td>
                     <td>{movement.pdv_name || movement.pdv_id}</td>
                     <td>{movement.doc_type}</td>
                     <td className="center">{movement.doc_number}</td>
                     <td className="center">
                        {MOVE_TYPE_LABELS[movement.move_type] ||
                           movement.move_type}
                     </td>
                     <td className="center">
                        {formatDateTimeSaoPaulo(movement.created_at)}
                     </td>
                  </tr>
               ))}
            </tbody>
         </Table>
      </TableWrapper>
   );
}

const duplicateShape = PropTypes.shape({
   id: PropTypes.number,
   pdv_id: PropTypes.number,
   pdv_name: PropTypes.string,
   move_type: PropTypes.string,
   doc_type: PropTypes.string,
   doc_number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
   created_at: PropTypes.string,
});

DuplicatesTable.propTypes = {
   duplicates: PropTypes.arrayOf(duplicateShape),
};

export default function DuplicateMovementsModal({
   isOpen,
   duplicates = [],
   queryInfo = {},
   onClose,
}) {
   const { pdvName, docType, docNumber } = queryInfo;

   const subtitle = [pdvName, docType, docNumber ? `nº ${docNumber}` : '']
      .filter(Boolean)
      .join(' · ');

   return (
      <Modal
         isOpen={isOpen}
         title="Possível duplicidade"
         subtitle={subtitle || null}
         onClose={onClose}
         footer={
            <GhostButton type="button" onClick={onClose}>
               Fechar
            </GhostButton>
         }
         maxWidth="760px"
      >
         <WarnBanner>
            Já existe{duplicates.length > 1 ? 'm' : ''} {duplicates.length}{' '}
            movimento{duplicates.length > 1 ? 's' : ''} ativo
            {duplicates.length > 1 ? 's' : ''} com o mesmo PDV, tipo e número
            de documento. Confira abaixo se não é um lançamento duplicado — o
            registro <strong>não</strong> é bloqueado.
         </WarnBanner>

         <DuplicatesTable duplicates={duplicates} />
      </Modal>
   );
}

DuplicateMovementsModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   duplicates: PropTypes.arrayOf(duplicateShape),
   queryInfo: PropTypes.shape({
      pdvName: PropTypes.string,
      docType: PropTypes.string,
      docNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   }),
   onClose: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Indicador discreto do estado do check, pra render abaixo do campo
// Nº Documento. Nada aparece em `idle`/`skipped` (falha degrada em silêncio).
// ---------------------------------------------------------------------------

const HintText = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   margin-top: 4px;
   font-size: 12px;
   font-weight: 600;
   color: ${({ $tone }) => {
      if ($tone === 'clear') return '#1b7f4d';
      if ($tone === 'found') return '#8a5a00';
      return 'rgba(0, 0, 0, 0.5)';
   }};
`;

export function DuplicateCheckHint({ status, count = 0, onOpen }) {
   if (status === 'checking') {
      return <HintText>Verificando duplicidade…</HintText>;
   }
   if (status === 'clear') {
      return <HintText $tone="clear">✓ Sem duplicidade registrada</HintText>;
   }
   if (status === 'found') {
      return (
         <HintText $tone="found">
            ⚠ {count} movimento{count > 1 ? 's' : ''} similar
            {count > 1 ? 'es' : ''}
            {onOpen ? (
               <TextLink
                  as="button"
                  type="button"
                  onClick={onOpen}
                  style={{ fontSize: 'inherit' }}
               >
                  ver
               </TextLink>
            ) : null}
         </HintText>
      );
   }
   return null; // idle | skipped
}

DuplicateCheckHint.propTypes = {
   status: PropTypes.oneOf(['idle', 'checking', 'clear', 'found', 'skipped'])
      .isRequired,
   count: PropTypes.number,
   onOpen: PropTypes.func,
};
